/**
 * `timetable` — the EVENT's public running order (all acts' set times).
 * Deliberately distinct from `schedule` (crew logistics) — ADR-007.
 */
import { z } from "zod";
import { defineCollection, field } from "../define.ts";

const WALL_TIME = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "expected HH:MM venue-local wall time");
const DAY = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD (venue-local)");

export const timetable = defineCollection({
  key: "timetable",
  labelKey: "collections.timetable",
  icon: "list-music",
  sheetTab: "Timetable",
  dedupFields: ["day", "start_time", "act"],
  sort: [{ field: "day" }, { field: "start_time" }],
  split: "day",
  chatHint:
    "The event's public running order: which act plays when, on which stage — including other " +
    "artists. NOT crew logistics (that's schedule). Promoters may see this; it carries no crew PII.",
  fields: {
    act: field({
      type: z.string().min(1),
      tier: "hot",
      pii: "promoter",
      labelKey: "timetable.act",
      sheet: { column: "Act" },
      description: "Act / artist name as billed on the lineup.",
    }),
    day: field({
      type: DAY.optional(),
      tier: "hot",
      pii: "promoter",
      labelKey: "timetable.day",
      renamedFrom: ["date"], // v1 wire name (migration lineage)
      sheet: { column: "Day" },
      description: "Date (YYYY-MM-DD, venue-local). Omit for single-day events.",
    }),
    start_time: field({
      type: WALL_TIME.optional(),
      tier: "hot",
      pii: "promoter",
      labelKey: "timetable.start_time",
      renamedFrom: ["start"],
      sheet: { column: "Start" },
      description: "Set start (HH:MM venue-local).",
    }),
    end_time: field({
      type: WALL_TIME.optional(),
      tier: "hot",
      pii: "promoter",
      labelKey: "timetable.end_time",
      renamedFrom: ["end"],
      sheet: { column: "End" },
      description: "Set end (HH:MM venue-local). Often unknown early — leave empty, never invent.",
    }),
    stage: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "promoter",
      labelKey: "timetable.stage",
      sheet: { column: "Stage" },
      description: "Stage / room / floor for multi-stage events.",
    }),
    is_us: field({
      type: z.boolean().optional(),
      tier: "detail",
      pii: "promoter",
      labelKey: "timetable.is_us",
      sheet: { column: "Us" },
      description: "True when this row is OUR set — highlighted in the UI and the FDR.",
    }),
    notes: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "promoter",
      labelKey: "timetable.notes",
      sheet: { column: "Notes" },
      description: "Notes on the slot (b2b, changeover, curfew).",
    }),
  },
});
